import { useCallback, useEffect, useState } from "react";

/**
 * This hook provides a store to manage the checkbox state of a table.
 * It also provides helper functions to manage the checkbox state.
 *
 * @param itemList - list of items to use to determine the checkbox state
 * @param key - key of the item to uniquely identify the item
 */
function useTableCheckboxStateManager<T>(itemList: T[]) {
  const [checkedItems, setCheckedItems] = useState<T[]>([]);
  const [headerChecked, setHeaderChecked] = useState(false);

  // helper function to get the check if if all items are checked or not
  const getItemsCheckState = useCallback(function (items: T[], checkedList: T[]) {
    const isEverythingChecked = items.every((item) => checkedList.includes(item));
    const isEverythingNotChecked = items.every((item) => !checkedList.includes(item));

    return {
      allChecked: isEverythingChecked,
      allNotChecked: isEverythingNotChecked,
    };
  }, []);

  /**
   * When the itemList changes, check if all items are checked. This effect will set the default state of the header checkbox.
   */
  useEffect(() => {
    // when item list changes, check if all item are checked
    if (itemList.length > 0) {
      const checkState = getItemsCheckState(itemList, checkedItems);
      /**
       * The header checkbox can be in one of three states:
       * 1. All items are checked: the header checkbox is checked and not indeterminate.
       * 2. No items are checked: the header checkbox is unchecked and not indeterminate.
       * 3. Some items are checked: the header checkbox is unchecked and indeterminate.
       */
      if (checkState.allChecked) {
        setHeaderChecked(true);
      } else {
        setHeaderChecked(false);
      }
    }
  }, [itemList]);

  /**
   * Check an item in the list, it has 3 states:
   * 1. If the all item on the page are checked, set the header checkbox to checked.
   * 2. If the all item on the page are unchecked, set the header checkbox to unchecked.
   * 3. If some items are checked, we leave it to the developer to decide if they need indeterminate state.
   */
  function checkItem(item: T) {
    // if the item is not checked, check it, and make the header indeterminate if not all items are checked
    setCheckedItems((prevCheckedItem) => {
      const updatedCheckedItems = [...prevCheckedItem, item];
      const checkState = getItemsCheckState(itemList, updatedCheckedItems);
      if (checkState.allChecked) {
        setHeaderChecked(true);
      } else if (checkState.allNotChecked) {
        setHeaderChecked(false);
      }
      return updatedCheckedItems;
    });
  }

  /**
   * Uncheck an item in the list, it has 2 states:
   * 1. If the all item on the page are unchecked, set the header checkbox to unchecked.
   * 2. If some items are checked, set the header checkbox to unchecked.
   */
  function uncheckItem(item: T) {
    setCheckedItems((prevCheckedItem) => {
      const updatedCheckedItems = prevCheckedItem.filter((prev) => prev !== item);
      const checkState = getItemsCheckState(itemList, updatedCheckedItems);
      if (checkState.allNotChecked) {
        setHeaderChecked(false);
      }

      return updatedCheckedItems;
    });
  }

  /**
   * Check items in the list.
   * If all items are checked, set the header checkbox to checked.
   * If some items in the ids are already checked, unchecked all the items and set the header checkbox to unchecked.
   * This is to handle the case when the checkboxes are in indeterminate state.
   * @param items
   */
  function checkItems(items: T[]) {
    const checkState = getItemsCheckState(itemList, checkedItems);

    // if checkboxes are in indeterminate state, uncheck items
    if (!checkState.allChecked && !checkState.allNotChecked) {
      uncheckItems(items);
      return;
    }

    const uniqueIds = items.filter((id) => !checkedItems.includes(id));
    setCheckedItems([...checkedItems, ...uniqueIds]);
    setHeaderChecked(true);
  }

  function uncheckItems(items: T[]) {
    setCheckedItems((prevCheckedItem) => {
      // filter out the ids from current page
      return prevCheckedItem.filter((id) => !items.includes(id));
    });
    setHeaderChecked(false);
  }

  /**
   * Reset the checkbox state to default state. It is helpful when the page is changed or table data is updated
   */
  function resetCheckState() {
    setCheckedItems([]);
    setHeaderChecked(false);
  }

  /**
   * Handle the checkbox state of the item.
   * @param checked - whether the item is checked or not
   * @param items - id of the item or list of ids. To select all items on the page, pass the list ids of all items on the page
   */
  function handleCheckItem(checked: boolean, items: T | T[]) {
    if (Array.isArray(items)) {
      checked ? checkItems(items) : uncheckItems(items);
    } else {
      checked ? checkItem(items) : uncheckItem(items);
    }
  }

  return {
    checkedItems,
    headerChecked,
    resetCheckState,
    handleCheckItem,
  };
}

export { useTableCheckboxStateManager };
